import { AuthError, type TwoFaChallenge } from "@vidario/api-client";
import { Button, Input, VidarioLogo } from "@vidario/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { z } from "zod";

import { useAuth } from "../../providers/auth-provider";

const loginSchema = z.object({
  email: z.string().email("Bitte eine gültige E-Mail-Adresse eingeben."),
  // Password rules apply when setting one, not when logging in (#23).
  password: z.string().min(1, "Bitte das Passwort eingeben."),
});
type LoginInput = z.infer<typeof loginSchema>;

function errorMessage(err: unknown): string {
  if (err instanceof AuthError) {
    switch (err.code) {
      case "invalid_credentials":
        return "E-Mail oder Passwort ist falsch.";
      case "invalid_2fa_code":
        return "Der Code ist ungültig. Bitte erneut versuchen.";
      case "challenge_expired":
        return "Anmeldung abgelaufen — bitte erneut anmelden.";
      case "email_not_confirmed":
        return "Bitte bestätige zuerst deine E-Mail-Adresse.";
      case "rate_limited":
        // Carries the wait from Retry-After (#67).
        return err.message;
      case "network":
        return "Netzwerkfehler — ist eine Internet-Verbindung aktiv?";
      default:
        return "Anmeldung fehlgeschlagen. Bitte erneut versuchen.";
    }
  }
  return "Unerwarteter Fehler. Bitte erneut versuchen.";
}

/** Step 1 — email + password. */
function CredentialsForm({
  notice,
  onChallenge,
  onAuthenticated,
}: {
  /** Why the user is here again: an expired session or 2FA challenge. */
  notice: string | null;
  onChallenge: (challenge: TwoFaChallenge) => void;
  onAuthenticated: () => Promise<void>;
}): ReactElement {
  const { signIn } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (values: LoginInput): Promise<void> => {
    setSubmitError(null);
    try {
      const result = await signIn(values.email, values.password);
      if (result.status === "two_fa_required") {
        onChallenge(result);
      } else {
        await onAuthenticated();
      }
    } catch (err) {
      setSubmitError(errorMessage(err));
    }
  };

  return (
    <>
      <h1 className="m-0 text-2xl font-semibold -tracking-[0.3px]">Anmelden</h1>
      <p className="mt-1 mb-6 text-sm text-text-secondary">
        Willkommen zurück bei Vidario.
      </p>

      {notice && (
        <p
          role="status"
          className="mb-4 rounded-md bg-warning-50 p-3 text-sm text-warning-700"
          data-testid="login-notice"
        >
          {notice}
        </p>
      )}

      <form
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
        className="flex flex-col gap-4"
        noValidate
      >
        <Input
          label="E-Mail"
          type="email"
          autoComplete="email"
          required
          {...register("email")}
          error={errors.email?.message}
        />
        <Input
          label="Passwort"
          type="password"
          autoComplete="current-password"
          required
          {...register("password")}
          error={errors.password?.message}
        />

        {submitError && (
          <p role="alert" className="text-sm text-danger-600" data-testid="login-error">
            {submitError}
          </p>
        )}

        <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
          Anmelden
        </Button>
      </form>

      <div className="mt-5 text-center text-sm">
        <Link
          to="/forgot-password"
          className="font-medium text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          Passwort vergessen?
        </Link>
      </div>
    </>
  );
}

/** Step 2 — TOTP two-factor verification. Shows the enrollment QR the
 * first time a user signs in; afterwards just the 6-digit code field. */
function TwoFaForm({
  challenge,
  onSuccess,
  onCancel,
  onExpired,
}: {
  challenge: TwoFaChallenge;
  onSuccess: () => Promise<void>;
  onCancel: () => void;
  /** The challenge timed out: back to step 1 with this message. */
  onExpired: (message: string) => void;
}): ReactElement {
  const { verifyTwoFa } = useAuth();
  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);
    try {
      await verifyTwoFa(challenge.ticket, code.trim(), { rememberDevice });
      await onSuccess();
    } catch (err) {
      if (err instanceof AuthError && err.code === "challenge_expired") {
        onExpired(errorMessage(err));
        return;
      }
      setSubmitError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <h1 className="m-0 text-2xl font-semibold -tracking-[0.3px]">
        Zwei-Faktor-Bestätigung
      </h1>
      {challenge.qrCode ? (
        <>
          <p className="mt-1 mb-4 text-sm text-text-secondary">
            Scanne diesen QR-Code mit einer Authenticator-App (z. B. Google
            Authenticator) und gib anschließend den angezeigten Code ein.
          </p>
          <div className="mb-5 flex justify-center">
            <img
              src={challenge.qrCode}
              alt="QR-Code zur Einrichtung der Authenticator-App"
              className="h-44 w-44 rounded border border-border-default"
            />
          </div>
        </>
      ) : (
        <p className="mt-1 mb-6 text-sm text-text-secondary">
          Gib den 6-stelligen Code aus deiner Authenticator-App ein.
        </p>
      )}

      <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
        <Input
          label="6-stelliger Code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        />

        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
            className="h-4 w-4 rounded border-border-default text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
          />
          Diesem Gerät 30 Tage vertrauen
        </label>

        {submitError && (
          <p role="alert" className="text-sm text-danger-600" data-testid="twofa-error">
            {submitError}
          </p>
        )}

        <Button
          type="submit"
          loading={submitting}
          disabled={submitting || code.length !== 6}
        >
          Bestätigen
        </Button>
      </form>

      <div className="mt-5 text-center text-sm">
        <button
          type="button"
          onClick={onCancel}
          className="font-medium text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          Zurück zur Anmeldung
        </button>
      </div>
    </>
  );
}

export function LoginRoute(): ReactElement {
  const { session, initializing } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [challenge, setChallenge] = useState<TwoFaChallenge | null>(null);
  const [notice, setNotice] = useState<string | null>(() =>
    (location.state as { reason?: string } | null)?.reason === "expired"
      ? "Sitzung abgelaufen – bitte erneut anmelden."
      : null,
  );

  if (initializing) return <></>;
  if (session) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? "/dashboard"} replace />;
  }

  const goToApp = async (): Promise<void> => {
    const from = (location.state as { from?: string } | null)?.from;
    await navigate(from ?? "/dashboard", { replace: true });
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-app px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center">
          <VidarioLogo size={36} />
        </div>
        <div className="rounded-lg border border-border-default bg-neutral-0 p-8 shadow-sm">
          {challenge ? (
            <TwoFaForm
              challenge={challenge}
              onSuccess={goToApp}
              onCancel={() => setChallenge(null)}
              onExpired={(message) => {
                setNotice(message);
                setChallenge(null);
              }}
            />
          ) : (
            <CredentialsForm
              notice={notice}
              onChallenge={setChallenge}
              onAuthenticated={goToApp}
            />
          )}
        </div>
      </div>
    </main>
  );
}
